/**
 * Реестр «я сам только что вышел из звонка в этой комнате» (Антон 15:51).
 *
 * ОТДЕЛЬНЫЙ МОДУЛЬ СПЕЦИАЛЬНО: логику вызывает `matrix/rtc.ts` (при выходе),
 * а читает `notifications/useIncomingCall.ts` (решая, звонить ли). Положить
 * её в любой из этих двух файлов = ЦИКЛИЧЕСКИЙ импорт, потому что
 * useIncomingCall уже импортирует managerFor из rtc. Сборка такой цикл
 * переживает, а в рантайме один из модулей может увидеть undefined вместо
 * функции — то есть звонок сломается молча и не там, где искали.
 *
 * Две жалобы, у которых ОДНА причина:
 *   1. «В группе трое, один вышел, двое остались — у вышедшего начинает
 *      звонить телефон».
 *   2. «Двое говорят, первый повесил трубку — у второго сначала гудок и
 *      только потом разъединяет, как будто звонок идёт к нему».
 *
 * Механика: при выходе снимается МОЁ членство, а чужие ещё живы. Для
 * describeIncoming это ровно картина входящего звонка — «я не в звонке,
 * другие разговаривают» — и он честно звонит. Во втором случае то же самое,
 * просто мой выход успевает записаться раньше ухода собеседника.
 *
 * Правило: после своего выхода звонок в этой комнате мне не показывается и
 * не звенит, пока сессия не закончится полностью (чужие членства не
 * исчезнут). Захочу вернуться — вернусь кнопкой сам.
 *
 * ЧИТАТЕЛЬ ПОЯВИЛСЯ 2026-10-04. До этой даты шапка выше ВРАЛА: модуль
 * писался (`rtc.ts` при выходе, `declineCall.ts` при отказе), но не читался
 * никем — `useIncomingCall.ts` про него не знал. Отсюда дефект «отклонил
 * входящий на Android, баннер вернулся через несколько секунд»: отметка
 * ставилась и ни на что не влияла.
 *
 * ПОЧЕМУ ХРАНИМ ID УВЕДОМЛЕНИЯ, А НЕ ТОЛЬКО ВРЕМЯ. Сравнение «пришло ли
 * уведомление ПОСЛЕ моего выхода» склеивает два РАЗНЫХ источника времени:
 * `sender_ts` ставит часы звонящего, а метку выхода — мои. Расхождение
 * часов между телефонами в пару секунд обычное дело, и тогда СТАРОЕ
 * уведомление выглядит «новее» моего выхода, то есть баннер возвращается.
 * Идентификатор события от часов не зависит: отклонённое уведомление
 * опознаётся точно, а повторный звонок создаёт НОВОЕ событие с новым id и
 * обязан пробиться.
 */

interface SelfLeftRecord {
  /** Когда я вышел/отклонил (мои локальные часы). */
  at: number;
  /** id уведомлений, которые я уже погасил; им звонить больше нельзя. */
  dismissedNotificationIds: Set<string>;
}

/** roomId -> запись о моём выходе. */
const selfLeftRooms = new Map<string, SelfLeftRecord>();

/**
 * Пауза после своего выхода, в течение которой звонок в этой комнате не
 * считается входящим ни при каких условиях. Закрывает «гудок при
 * разъединении» в 1:1: мой выход и уход собеседника приходят не одновременно.
 */
export const SELF_LEAVE_GRACE_MS = 3000;

/** Я вышел из звонка в этой комнате — больше он мне не звонит. */
export function markSelfLeftCall(roomId: string): void {
  const existing = selfLeftRooms.get(roomId);
  if (existing) existing.at = Date.now();
  else selfLeftRooms.set(roomId, { at: Date.now(), dismissedNotificationIds: new Set() });
  console.info(`[call] self-left room=${roomId} -> incoming suppressed`);
}

/**
 * Это конкретное уведомление я погасил сам (отклонил входящий).
 *
 * Вызывается СИНХРОННО, до любой сети: гашение баннера не имеет права
 * зависеть от того, доехал ли hangup до сервера.
 */
export function noteDismissedCallNotification(roomId: string, notificationEventId: string): void {
  let record = selfLeftRooms.get(roomId);
  if (!record) {
    record = { at: Date.now(), dismissedNotificationIds: new Set() };
    selfLeftRooms.set(roomId, record);
  }
  record.dismissedNotificationIds.add(notificationEventId);
  console.info(`[call] dismissed notification room=${roomId} event=${notificationEventId}`);
}

/** Метка времени моего выхода, либо undefined если я не выходил. */
export function selfLeftAt(roomId: string): number | undefined {
  return selfLeftRooms.get(roomId)?.at;
}

/**
 * Должен ли входящий звонок в этой комнате молчать.
 *
 * @param notificationEventId id САМОГО СВЕЖЕГО уведомления о звонке.
 * @param notificationTs его время по часам отправителя (запасной критерий).
 */
export function isIncomingSuppressed(
  roomId: string,
  notificationEventId: string | null,
  notificationTs: number,
): boolean {
  const record = selfLeftRooms.get(roomId);
  if (!record) return false;
  // 1. Только что вышел: мой выход и уход собеседника приходят не
  //    одновременно — это окно и закрывает «гудок при разъединении».
  if (Date.now() - record.at < SELF_LEAVE_GRACE_MS) return true;
  // 2. Точное совпадение: ровно это уведомление я и отклонил. От часов
  //    не зависит, поэтому главный критерий — он.
  if (notificationEventId && record.dismissedNotificationIds.has(notificationEventId)) return true;
  // 3. Запас для выхода через teardown, где id уведомления неизвестен:
  //    нового звонка не было, значит звонить нечему. SELF_LEAVE_GRACE_MS
  //    добавлен как допуск на расхождение часов между устройствами.
  return notificationTs <= record.at + SELF_LEAVE_GRACE_MS;
}

/** Сессия звонка закончилась полностью — забываем отметку. */
export function clearSelfLeft(roomId: string): void {
  if (selfLeftRooms.delete(roomId)) {
    console.info(`[call] session ended room=${roomId} -> incoming allowed again`);
  }
}

/**
 * Я снова вошёл в звонок сам — отметка не нужна. Иначе после «вышел ->
 * вернулся -> вышел» первая отметка осталась бы висеть навсегда.
 */
export function clearSelfLeftOnRejoin(roomId: string): void {
  selfLeftRooms.delete(roomId);
}
